import { VERIFICATION_CHECKLIST_ITEMS, type RecordVerificationInput, type VerificationChecklistItem, type VerificationResult } from '@chukua/common';

export const CHECKLIST_LABELS: Record<VerificationChecklistItem, string> = {
  work_matches_order: 'The work done matches the order',
  defect_cleared: 'The defect is cleared',
  no_new_damage: 'There is no new damage',
  road_ready: 'The truck is ready for the road',
};

export interface ChecklistAnswer {
  result: VerificationResult | null;
  note: string;
}

export type VerificationFormValues = Record<VerificationChecklistItem, ChecklistAnswer> & { reason: string; photoUploadIds: string[] };

export function blankVerificationForm(): VerificationFormValues {
  const answers = Object.fromEntries(VERIFICATION_CHECKLIST_ITEMS.map((item) => [item, { result: null, note: '' }])) as Record<
    VerificationChecklistItem,
    ChecklistAnswer
  >;
  return { ...answers, reason: '', photoUploadIds: [] };
}

export type VerificationPreview = 'incomplete' | 'close' | 'send_back';

/** What submitting would do. Mirrors the API's verification-outcome.policy.ts: any fail sends the order back, all four passes close it. */
export function previewVerification(values: VerificationFormValues): VerificationPreview {
  const results = VERIFICATION_CHECKLIST_ITEMS.map((item) => values[item].result);
  if (results.includes('fail')) return 'send_back';
  return results.every((result) => result === 'pass') ? 'close' : 'incomplete';
}

/** Why Submit is disabled, or null when the server would accept it: a pass needs a photo, a fail needs the reason. */
export function verificationBlocker(values: VerificationFormValues): string | null {
  const preview = previewVerification(values);
  if (preview === 'incomplete') return 'Answer all four checks.';
  if (preview === 'send_back') return values.reason.trim() === '' ? 'Say why the work failed.' : null;
  return values.photoUploadIds.length === 0 ? 'Add at least one photo to close the order.' : null;
}

export function toVerificationInput(values: VerificationFormValues): RecordVerificationInput {
  return {
    checklist: VERIFICATION_CHECKLIST_ITEMS.map((item) => {
      const { result, note } = values[item];
      return { item, result: result ?? 'fail', ...(note.trim() ? { note: note.trim() } : {}) };
    }),
    ...(values.reason.trim() ? { reason: values.reason.trim() } : {}),
    photoUploadIds: values.photoUploadIds,
  };
}
